import { api } from '../api.ts';
import { compact, html, mount, raw } from '../html.ts';
import { formatDate, isOverdue, options, PRIORITY, t, TASK_STATUS } from '../i18n.ts';
import { setQuery } from '../router.ts';
import { canWrite, lookups } from '../state.ts';
import { openCommentThread, renderComments } from './comments.ts';
import { renderLinks } from './links.ts';
import { confirmDialog, emptyState, field, openFormDialog, optionList, select, statusBadge, textarea, toast, toastError } from '../ui.ts';

/**
 * Task board (kanban) and list. The board is an app-local prototype of a
 * Purple UI "board" pattern: native buttons as cards, pointer drag and drop,
 * and keyboard moves (Alt+←/→ between columns, Alt+↑/↓ within a column)
 * announced through a live region.
 */

const BOARD_COLUMNS = ['todo', 'in_progress', 'blocked', 'done'] as const;

interface Task {
  id: string;
  number: number;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  assignee_user_id: string | null;
  delegated_contact_id: string | null;
  phase_id: string | null;
  starts_on: string | null;
  due_on: string | null;
  position: number;
}

interface Context {
  projectId: string;
  projectKey: string;
  phases: { id: string; label: string }[];
}

async function taskFields(ctx: Context, task: Partial<Task> = {}) {
  const [members, contacts] = await Promise.all([lookups.members(), lookups.contacts()]);
  return html`
    ${field(t('Titel'), 'title', 'text', { required: true, value: task.title, span: true })}
    ${select(t('Status'), 'status', options(TASK_STATUS, task.status ?? 'todo'))}
    ${select(t('Priorität'), 'priority', options(PRIORITY, task.priority ?? 'medium'))}
    ${select(t('Zuständig'), 'assignee_user_id', optionList(members, task.assignee_user_id, t('Niemand')))}
    ${select(t('Delegiert an Kontakt'), 'delegated_contact_id', optionList(contacts, task.delegated_contact_id, t('Nicht delegiert')))}
    ${select(t('Phase'), 'phase_id', optionList(ctx.phases, task.phase_id, t('Keine Phase')))}
    ${field(t('Fällig'), 'due_on', 'date', { value: task.due_on })}
    ${textarea(t('Beschreibung'), 'description', { value: task.description, rows: 5 })}
  `;
}

export async function openTaskDialog(ctx: Context, task: Task | null, onChange: () => void) {
  const dialog = openFormDialog({
    title: task ? t('{key} bearbeiten', { key: `${ctx.projectKey}-${task.number}` }) : t('Neue Aufgabe'),
    wide: true,
    fields: await taskFields(ctx, task ?? {}),
    submitLabel: task ? t('Speichern') : t('Anlegen'),
    onSubmit: async (data) => {
      if (task) await api.patch(`/tasks/${task.id}`, data);
      else await api.post(`/projects/${ctx.projectId}/tasks`, compact(data));
      toast(task ? t('Aufgabe gespeichert') : t('Aufgabe angelegt'));
      onChange();
    },
  });
  if (task) {
    const slot = document.createElement('section');
    slot.className = 'pma-span-2';
    dialog.querySelector('.pma-form-grid')!.append(slot);
    void renderLinks(slot, 'task', task.id);
    const thread = document.createElement('section');
    thread.className = 'pma-span-2';
    dialog.querySelector('.pma-form-grid')!.append(thread);
    void renderComments(thread, 'task', task.id);
  }
  if (task && canWrite()) {
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'p-button p-button--ghost pma-danger-text';
    remove.textContent = t('Löschen');
    remove.addEventListener('click', async () => {
      dialog.close();
      if (!(await confirmDialog(t('Aufgabe löschen?'), t('{key} „{title}“ wird endgültig gelöscht.', { key: `${ctx.projectKey}-${task.number}`, title: task.title })))) return;
      await api.delete(`/tasks/${task.id}`).catch(toastError);
      onChange();
    });
    dialog.querySelector('.p-dialog__actions')!.prepend(remove);
  }
}

function card(ctx: Context, task: Task, people: Map<string, string>) {
  const who = task.assignee_user_id ? people.get(task.assignee_user_id) : task.delegated_contact_id ? `→ ${people.get(task.delegated_contact_id) ?? t('Kontakt')}` : '';
  return html`<li class="pma-board__item" data-id="${task.id}">
    <button type="button" class="pma-card" draggable="${canWrite() ? 'true' : 'false'}" data-task="${task.id}"
      aria-describedby="pma-board-help">
      <span class="pma-card__key">${ctx.projectKey}-${task.number}</span>
      <span class="pma-card__title">${task.title}</span>
      <span class="pma-card__meta">
        ${task.priority === 'high' || task.priority === 'urgent' ? statusBadge(task.priority, PRIORITY) : ''}
        ${task.due_on ? html`<span class="${isOverdue(task.due_on) && task.status !== 'done' ? 'pma-overdue' : ''}">${formatDate(task.due_on)}</span>` : ''}
        ${who ? html`<span class="pma-muted">${who}</span>` : ''}
      </span>
    </button>
  </li>`;
}

/** Position between neighbours so only the moved task needs an update. */
function positionAt(tasks: Task[], index: number): number {
  const before = tasks[index - 1]?.position;
  const after = tasks[index]?.position;
  if (before === undefined && after === undefined) return 1;
  if (before === undefined) return after! - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
}

export async function renderTasks(target: HTMLElement, ctx: Context, query: URLSearchParams) {
  let view = query.get('view') === 'list' ? 'list' : 'board';
  let phaseFilter = query.get('phase') ?? '';
  let tasks: Task[] = [];
  const [members, contacts] = await Promise.all([lookups.members(), lookups.contacts()]);
  const people = new Map([...members, ...contacts].map((p) => [p.id, p.label]));

  async function load() {
    tasks = (await api.get<{ items: Task[] }>(`/projects/${ctx.projectId}/tasks?limit=500`)).items;
    draw();
  }

  function visible() {
    return tasks.filter((x) => !phaseFilter || x.phase_id === phaseFilter);
  }

  function draw() {
    const list = visible();
    mount(
      target,
      html`<div class="p-table-toolbar pma-toolbar">
        <fieldset class="p-segmented"><legend class="p-visually-hidden">${t('Ansicht')}</legend>
          <div class="p-segmented__group">
            <label><input type="radio" name="task-view" value="board"${view === 'board' ? raw(' checked') : ''}><span>${t('Board')}</span></label>
            <label><input type="radio" name="task-view" value="list"${view === 'list' ? raw(' checked') : ''}><span>${t('Liste')}</span></label>
          </div></fieldset>
        ${ctx.phases.length ? html`<label class="p-field pma-inline-field"><span class="p-label">${t('Phase')}</span>
          <select class="p-select" data-p-size="sm" data-phase-filter>${raw(optionList(ctx.phases, phaseFilter, t('Alle Phasen')))}</select></label>` : ''}
        <span class="p-table-toolbar__status pma-muted">${t('{open} offen · {total} gesamt', { open: list.filter((x) => x.status !== 'done' && x.status !== 'cancelled').length, total: list.length })}</span>
        <div class="p-table-toolbar__actions">${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-new>${t('Neue Aufgabe')}</button>` : ''}</div>
      </div>
      <p id="pma-board-help" class="p-visually-hidden">${t('Enter öffnet die Aufgabe. Alt und Pfeiltasten verschieben sie zwischen und innerhalb der Spalten.')}</p>
      <p class="p-visually-hidden" role="status" aria-live="polite" data-announce></p>
      ${
        !tasks.length
          ? emptyState(t('Noch keine Aufgaben'), t('Lege die erste Aufgabe an oder nutze eine Projektvorlage.'))
          : view === 'board'
            ? html`<div class="pma-board">${BOARD_COLUMNS.map((status) => {
                const column = list.filter((x) => x.status === status);
                return html`<section class="pma-board__column" data-status="${status}" aria-labelledby="col-${status}">
                  <header class="pma-board__header"><h3 id="col-${status}">${TASK_STATUS[status]}</h3><span class="pma-muted">${column.length}</span></header>
                  <ol class="pma-board__list" data-drop="${status}">${column.map((x) => card(ctx, x, people))}</ol>
                </section>`;
              })}</div>`
            : html`<div class="p-table-wrap"><table class="p-table pma-table"><caption class="p-visually-hidden">${t('Aufgaben')}</caption>
                <thead><tr><th scope="col">${t('Nr.')}</th><th scope="col">${t('Titel')}</th><th scope="col">${t('Status')}</th><th scope="col">${t('Priorität')}</th><th scope="col">${t('Zuständig')}</th><th scope="col">${t('Fällig')}</th></tr></thead>
                <tbody>${list.map(
                  (x) => html`<tr>
                    <td data-label="${t('Nr.')}">${ctx.projectKey}-${x.number}</td>
                    <td data-label="${t('Titel')}"><button type="button" class="p-button p-button--link" data-task="${x.id}">${x.title}</button></td>
                    <td data-label="${t('Status')}">${statusBadge(x.status, TASK_STATUS)}</td>
                    <td data-label="${t('Priorität')}">${statusBadge(x.priority, PRIORITY)}</td>
                    <td data-label="${t('Zuständig')}">${(x.assignee_user_id && people.get(x.assignee_user_id)) || (x.delegated_contact_id && `→ ${people.get(x.delegated_contact_id)}`) || '–'}</td>
                    <td data-label="${t('Fällig')}"><span class="${isOverdue(x.due_on) && x.status !== 'done' ? 'pma-overdue' : ''}">${formatDate(x.due_on)}</span></td>
                  </tr>`,
                )}</tbody></table></div>`
      }`,
    );
  }

  async function move(task: Task, status: string, index: number) {
    const column = tasks.filter((x) => x.status === status && x.id !== task.id).sort((a, b) => a.position - b.position);
    const position = positionAt(column, Math.max(0, Math.min(index, column.length)));
    const previous = { status: task.status, position: task.position };
    task.status = status;
    task.position = position;
    tasks.sort((a, b) => a.position - b.position);
    draw();
    target.querySelector<HTMLElement>(`[data-task="${task.id}"]`)?.focus();
    const announce = target.querySelector('[data-announce]');
    if (announce) announce.textContent = t('{key} in {status}, Position {n}', { key: `${ctx.projectKey}-${task.number}`, status: TASK_STATUS[status], n: index + 1 });
    try {
      const saved = await api.patch<Task>(`/tasks/${task.id}`, previous.status === status ? { position } : { status, position });
      Object.assign(task, saved);
    } catch (error) {
      Object.assign(task, previous);
      draw();
      toastError(error);
    }
  }

  target.addEventListener('change', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.name === 'task-view') {
      view = input.value;
      setQuery('view', view === 'board' ? null : view);
      draw();
    }
    if (input.matches('[data-phase-filter]')) {
      phaseFilter = input.value;
      setQuery('phase', phaseFilter || null);
      draw();
    }
  });

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void openTaskDialog(ctx, null, load);
    const id = element.closest<HTMLElement>('[data-task]')?.dataset.task;
    const task = tasks.find((x) => x.id === id);
    if (task) canWrite() ? void openTaskDialog(ctx, task, load) : openCommentThread('task', task.id, `${ctx.projectKey}-${task.number} ${task.title}`);
  });

  target.addEventListener('keydown', (event) => {
    if (!event.altKey || !canWrite()) return;
    const id = (event.target as HTMLElement).closest<HTMLElement>('.pma-card')?.dataset.task;
    const task = tasks.find((x) => x.id === id);
    if (!task || !BOARD_COLUMNS.includes(task.status as any)) return;
    const columnIndex = BOARD_COLUMNS.indexOf(task.status as (typeof BOARD_COLUMNS)[number]);
    const column = visible().filter((x) => x.status === task.status);
    const index = column.indexOf(task);
    if (event.key === 'ArrowRight' && columnIndex < BOARD_COLUMNS.length - 1) void move(task, BOARD_COLUMNS[columnIndex + 1]!, 0);
    else if (event.key === 'ArrowLeft' && columnIndex > 0) void move(task, BOARD_COLUMNS[columnIndex - 1]!, 0);
    else if (event.key === 'ArrowUp' && index > 0) void move(task, task.status, index - 1);
    else if (event.key === 'ArrowDown' && index < column.length - 1) void move(task, task.status, index + 1);
    else return;
    event.preventDefault();
  });

  // Pointer drag and drop.
  let dragged: Task | undefined;
  target.addEventListener('dragstart', (event) => {
    const id = (event.target as HTMLElement).closest<HTMLElement>('[data-task]')?.dataset.task;
    dragged = tasks.find((x) => x.id === id);
    if (dragged && event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', dragged.id);
    }
  });
  target.addEventListener('dragover', (event) => {
    const list = (event.target as HTMLElement).closest<HTMLElement>('[data-drop]');
    if (!list || !dragged) return;
    event.preventDefault();
    for (const other of target.querySelectorAll('.pma-board__list--over')) other.classList.remove('pma-board__list--over');
    list.classList.add('pma-board__list--over');
  });
  target.addEventListener('dragend', () => {
    for (const other of target.querySelectorAll('.pma-board__list--over')) other.classList.remove('pma-board__list--over');
    dragged = undefined;
  });
  target.addEventListener('drop', (event) => {
    const list = (event.target as HTMLElement).closest<HTMLElement>('[data-drop]');
    if (!list || !dragged) return;
    event.preventDefault();
    const items = [...list.querySelectorAll<HTMLElement>('.pma-board__item')].filter((item) => item.dataset.id !== dragged!.id);
    let index = items.length;
    for (const [i, item] of items.entries()) {
      const box = item.getBoundingClientRect();
      if (event.clientY < box.top + box.height / 2) {
        index = i;
        break;
      }
    }
    void move(dragged, list.dataset.drop!, index);
  });

  await load();
}
